import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.launch

private val SEASON_STATUSES = listOf("draft" to "Připravovaná", "active" to "Probíhá", "finished" to "Ukončená")

private fun statusLabel(status: String): String = SEASON_STATUSES.find { it.first == status }?.second ?: status

@Composable
fun SeasonsScreen(token: String?, role: String?, onOpenLeagues: (seasonId: String, seasonName: String) -> Unit) {
    var seasons by remember { mutableStateOf<List<SeasonDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var refreshKey by remember { mutableStateOf(0) }

    var name by remember { mutableStateOf("") }
    var startDate by remember { mutableStateOf("") }
    var previousSeasonId by remember { mutableStateOf("") }
    var promotionCount by remember { mutableStateOf("2") }

    var deletingSeasonId by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    LaunchedEffect(refreshKey) {
        loading = true
        try {
            seasons = Api.fetchSeasons(token)
            error = null
        } catch (e: Throwable) {
            error = "Načtení sezón selhalo: ${e.message}"
        }
        loading = false
    }

    Div(attrs = { classes("card") }) {
        H2 { Text("Sezóny") }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }

        Table {
            Tr {
                Th { Text("Název") }
                Th { Text("Start") }
                Th { Text("Stav") }
                Th { Text("Aktivní") }
                Th { }
            }
            seasons.forEach { season ->
                val confirmingDelete = deletingSeasonId == season.id
                Tr {
                    Td(attrs = { onClick { onOpenLeagues(season.id, season.name) } }) { Text(season.name) }
                    Td { Text(formatDate(season.startDate)) }
                    Td {
                        if (!confirmingDelete && role.isSuperadmin()) {
                            Select(attrs = {
                                onChange { ev ->
                                    ev.value?.let { newStatus ->
                                        scope.launch {
                                            try {
                                                Api.setSeasonStatus(token, season.id, newStatus)
                                                refreshKey++
                                            } catch (e: Throwable) {
                                                error = "Změna stavu selhala: ${e.apiMessage()}"
                                            }
                                        }
                                    }
                                }
                            }) {
                                SEASON_STATUSES.forEach { (value, label) ->
                                    Option(value, attrs = { if (value == season.status) selected() }) { Text(label) }
                                }
                            }
                        } else {
                            Text(statusLabel(season.status))
                        }
                    }
                    Td {
                        Text(if (season.isActive) "ano " else "ne ")
                        if (!confirmingDelete && role.isSuperadmin()) {
                            if (season.isActive) {
                                Button(attrs = {
                                    onClick {
                                        scope.launch {
                                            try {
                                                Api.clearDefaultSeason(token)
                                                refreshKey++
                                            } catch (e: Throwable) {
                                                error = "Zrušení výchozí sezóny selhalo: ${e.apiMessage()}"
                                            }
                                        }
                                    }
                                }) { Text("Zrušit výchozí") }
                            } else {
                                Button(attrs = {
                                    onClick {
                                        scope.launch {
                                            try {
                                                Api.setDefaultSeason(token, season.id)
                                                refreshKey++
                                            } catch (e: Throwable) {
                                                error = "Nastavení výchozí sezóny selhalo: ${e.apiMessage()}"
                                            }
                                        }
                                    }
                                }) { Text("Nastavit jako výchozí") }
                            }
                        }
                    }
                    if (confirmingDelete) {
                        Td {
                            Text("Opravdu nevratně smazat sezónu „${season.name}“ včetně všech lig, zápasů, výsledků a soupisek? ")
                            Button(attrs = {
                                onClick {
                                    scope.launch {
                                        try {
                                            Api.deleteSeason(token, season.id)
                                            deletingSeasonId = null
                                            refreshKey++
                                        } catch (e: Throwable) {
                                            error = "Smazání selhalo: ${e.apiMessage()}"
                                        }
                                    }
                                }
                            }) { Text("Ano, smazat") }
                            Button(attrs = { onClick { deletingSeasonId = null } }) { Text("Zrušit") }
                        }
                    } else {
                        Td {
                            if (season.status != "active" && role.isSuperadmin()) {
                                Button(attrs = { onClick { deletingSeasonId = season.id } }) { Text("Smazat") }
                            }
                        }
                    }
                }
            }
        }

        if (role.isSuperadmin()) {
            H3 { Text("Nová sezóna") }
            Div {
                Input(type = InputType.Text) {
                    placeholder("Název, např. 2026/27")
                    value(name)
                    onInput { name = it.value }
                }
                Input(type = InputType.Date) {
                    value(startDate)
                    onInput { startDate = it.value }
                }
                Div {
                    Text("Výběr předcházející sezóny (nepovinné): ")
                    Select(attrs = { onChange { previousSeasonId = it.value ?: "" } }) {
                        Option("") { Text("— žádná (prázdná sezóna) —") }
                        seasons.filter { it.status == "finished" }.forEach { s ->
                            Option(s.id) { Text(s.name) }
                        }
                    }
                }
                if (previousSeasonId.isNotBlank()) {
                    Div {
                        Text("Počet postupujících/sestupujících týmů: ")
                        Input(type = InputType.Text) {
                            value(promotionCount)
                            onInput { promotionCount = it.value }
                        }
                    }
                }
                Button(attrs = {
                    onClick {
                        scope.launch {
                            try {
                                if (previousSeasonId.isBlank()) {
                                    Api.createSeason(token, name, startDate)
                                } else {
                                    val count = promotionCount.toIntOrNull()
                                    if (count == null) {
                                        error = "Počet postupujících/sestupujících musí být číslo"
                                        return@launch
                                    }
                                    Api.cloneSeason(token, name, startDate, previousSeasonId, count)
                                }
                                name = ""
                                startDate = ""
                                previousSeasonId = ""
                                promotionCount = "2"
                                refreshKey++
                            } catch (e: Throwable) {
                                error = "Založení selhalo: ${e.apiMessage()}"
                            }
                        }
                    }
                }) { Text("Založit sezónu") }
            }
        }
    }
}
